<template>
    <button :class="pickrId" @click="onClick" v-if="$slots.trigger">
        <slot name="trigger" />
    </button>
    <activix-button
        class="relative"
        :class="[clickedClass, pickrId, classes]"
        :disabled="disabled"
        :size="size"
        @click="onClick"
        v-tooltip="disabled ? '' : tooltipContent"
        v-else
    >
        <div class="ml-2">
            <icon name="regular/color-brush-3" class="w-4 h-4" v-if="!$slots.icon" />
            <slot name="icon" />
        </div>
        <span
            class="absolute left-0 ml-2 rounded-full"
            :class="colorHeigthClass"
            :style="{ width: '0.25rem', 'background-color': color }"
            v-if="selectedColor"
        />
    </activix-button>
</template>

<script>
    import { v4 as uuidv4 } from 'uuid';
    import '@simonwep/pickr/dist/themes/nano.min.css';
    import Pickr from '@simonwep/pickr';

    import { ActivixButton } from '@/components/activix-ui/index.ts';

    export const colors = ['white', 'light-gray', 'gray', 'dark-gray'];
    export const sizes = ['xs', 'sm', 'md', 'lg', 'xl'];
    export const modes = ['swatch-only', 'picker-only', 'default'];

    export default {
        components: {
            ActivixButton,
        },

        emits: ['input'],

        props: {
            mode: {
                type: String,
                default: 'default',
                validator: (value) => modes.includes(value),
            },
            swatches: {
                type: Array,
                default: () => [
                    '#FDFEFE',
                    '#BDC3C7',
                    '#95A5A6',
                    '#7F8C8D',
                    '#1ABC9C',
                    '#27AE60',
                    '#16A085',
                    '#2980B9',
                    '#3498DB',
                    '#9B59B6',
                    '#8E44AD',
                    '#F1C40F',
                    '#F39C12',
                    '#E74C3C',
                    '#2C3E50',
                ],
            },
            buttonColor: {
                type: String,
                default: 'gray',
                validator: (value) => colors.includes(value),
            },
            size: {
                type: String,
                default: 'md',
                validator: (value) => sizes.includes(value),
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            tooltipContent: {
                type: String,
                default: '',
            },
            selectedColor: {
                type: String,
                default: 'black',
            },
            classes: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                pickrId: 'pickr-',
                isClicked: false,
                color: 'black',
                pickrInstance: null,
            };
        },

        computed: {
            clickedClass() {
                return this.isClicked ? 'bg-white shadow-lg' : '';
            },
            colorHeigthClass() {
                return ['xs', 'sm'].includes(this.size) ? 'h-4' : 'h-6';
            },
        },

        watch: {
            selectedColor(newValue) {
                this.color = newValue;
            },
        },

        methods: {
            init() {
                this.pickrInstance = Pickr.create({
                    el: `.${this.pickrId}`,
                    theme: 'nano',
                    useAsButton: true,
                    default: this.color,
                    swatches: ['picker-only'].includes(this.mode) ? [] : this.swatches,
                    appClass: `activix-picker ${this.mode}`,
                    components: {
                        preview: ['default', 'picker-only'].includes(this.mode),
                        opacity: ['default', 'picker-only'].includes(this.mode),
                        hue: ['default', 'picker-only'].includes(this.mode),
                        palette: ['default', 'picker-only'].includes(this.mode),
                    },
                });

                this.pickrInstance
                    .on('change', (color) => {
                        this.setColor(color.toHEXA().toString());
                    })
                    .on('hide', () => {
                        this.destroy();
                    })
                    .on('swatchselect', (color) => {
                        this.setColor(color.toHEXA().toString());
                        this.destroy();
                    });

                this.pickrInstance.show();
            },

            async destroy() {
                await this.$nextTick();

                if (!this.pickrInstance) {
                    return;
                }

                this.pickrInstance.destroyAndRemove();
                this.pickrInstance = null;
                this.isClicked = false;
            },

            onClick() {
                this.isClicked = !this.isClicked;

                if (this.pickrInstance) {
                    this.destroy();
                }

                this.init();
            },

            setColor(color) {
                this.color = color;
                this.$emit('input', this.color);
            },
        },

        mounted() {
            this.pickrId = `pickr-${uuidv4().replace(/[.-]/g, '')}`;
            this.color = this.selectedColor;
        },

        beforeUnmount() {
            this.destroy();
        },
    };
</script>

<style lang="less">
    .activix-picker {
        .picker-only .pcr-swatches {
            @apply hidden m-0 p-0 !important;
        }
        .pcr-app {
            @apply rounded shadow-lg !important;
        }
        .pcr-swatches button::after {
            @apply h-full border rounded-sm bg-opacity-0 !important;
            -webkit-box-shadow: inset 6px 2px 21px -21px rgba(0, 0, 0, 0.65);
            box-shadow: inset 6px 2px 21px -21px rgba(0, 0, 0, 0.65);
        }
        .pcr-swatches > button {
            @apply text-xl !important;
        }
        .pcr-active {
            @apply rounded-sm !important;
        }
        .pcr-selection,
        .pcr-color-palette {
            @apply border-b rounded-b-none mb-6 !important;
        }
        .pcr-color-palette {
            @apply rounded-b-none !important;
        }
        .pcr-color-chooser .pcr-picker,
        .pcr-color-opacity .pcr-picker {
            @apply h-5 w-5 rounded-full shadow-lg border-2 border-gray-300 bg-white !important;
        }
        .pcr-color-palette .pcr-picker {
            @apply shadow-md !important;
        }
        .pcr-last-color,
        .pcr-color-preview::before,
        .pcr-palette::before,
        .pcr-swatches button::before,
        .pcr-interaction {
            @apply hidden;
        }
        .pcr-current-color {
            @apply rounded h-10 w-10 !important;
        }
        .pcr-palette {
            @apply rounded-t shadow-md !important;
        }
        .pcr-swatches {
            @apply grid grid-cols-5 gap-1 bg-gray-150 py-3 mt-0 border-t-2 border-l-0 border-b-0 border-r-0 border-solid border-gray-300 !important;
        }
        .swatch-only .pcr-swatches {
            @apply border-none !important;
        }
    }
</style>
